import {
  ALERT_SECTION_LABELS,
  Alert,
  AlertSection,
  AlertType,
  AlertsCommentary,
  nonDismissableAlertTypes,
} from '@aloft/toolkit-shared';
import { Button, Flex, Input, Typography, useThemeColor } from '@aloft/toolkit-sui';

import { debounce, isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useZodForm } from '../../../hooks/useZodForm.ts';
import { cancelDismissAlert, dismissAlert } from '../../../redux/features/alertsSlice.ts';
import { setCommentaryV2 } from '../../../redux/features/commentarySlice.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsInspectifyOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { useAlerts } from './hooks/useAlerts.tsx';

const AlertItem = ({ alert, index }: { alert: Alert; index: number }) => {
  const {
    type,
    status,
    message,
    fixingInfo: { tips, pairedCostRouteItem },
  } = alert;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);

  const { control } = useFormContext<AlertsCommentary>();

  const isDismissed = status === 'dismissed';
  const isNonDismissable = nonDismissableAlertTypes.includes(type);

  return (
    <Flex key={type} flexDirection="column" gap="0.25rem">
      <Flex justifyContent="space-between" alignItems="center" gap="0.5rem" flexGrow={1}>
        <Typography variant="body1">
          {index + 1}. {message}
        </Typography>
        <Flex alignItems="center" gap="0.5rem" whiteSpace="nowrap">
          {isDismissed && !isNonDismissable && (
            <Button
              variant="inline"
              size="sm"
              text="Cancel Dismiss"
              // Leaving the alert commentary intact in case the user re-enables the alert later
              onClick={() => dispatch(cancelDismissAlert(type))}
            />
          )}
          {!isDismissed && (
            <>
              {isInspectifyOrder && (
                <Button
                  size="sm"
                  text="Fix Now"
                  onClick={() => navigate(`paired-cost/${pairedCostRouteItem}`)}
                />
              )}
              {!isNonDismissable && (
                <Button
                  variant="secondary"
                  size="sm"
                  text="Dismiss"
                  onClick={() => dispatch(dismissAlert(type))}
                />
              )}
            </>
          )}
        </Flex>
      </Flex>
      {isDismissed && (
        <Controller
          name={type}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <Input
                type="multiline"
                resize="vertical"
                __dangerDoNotUseSx={
                  error ? { borderColor: useThemeColor('alertError') } : undefined
                }
                value={value}
                onClear={() => onChange('')}
                onChange={onChange}
              />
              {error && (
                <Typography variant="body1" color="alertError">
                  {error.message}
                </Typography>
              )}
            </>
          )}
        />
      )}
      {!isDismissed && (
        <Typography variant="body2" color="surfaceAccent4">
          {tips}
        </Typography>
      )}
    </Flex>
  );
};

export const AlertsContent = ({ alerts }: { alerts: ReturnType<typeof useAlerts> }) => {
  const dispatch = useAppDispatch();

  const commentaryV2 = useAppSelector(selectCommentaryV2);

  const dismissedAlertTypes = useMemo(
    () =>
      Object.entries(alerts)
        .flatMap(([_section, sectionAlerts]) => sectionAlerts)
        .filter(({ status }) => status === 'dismissed')
        .map(({ type }) => type),
    [alerts],
  );

  const schDismissedAlertsCommentary = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          dismissedAlertTypes.map((type) => [type, z.string().min(1, 'This field is required')]),
        ),
      ),
    [dismissedAlertTypes],
  );

  const zodForm = useZodForm({
    schema: schDismissedAlertsCommentary,
    defaultValues: useMemo(() => commentaryV2.alerts ?? {}, []),
    mode: 'all',
  });

  const { watch } = zodForm;

  const debouncedSetCommentary = useMemo(
    () =>
      debounce((newAlertsCommentary: AlertsCommentary | undefined) => {
        if (isEqual(newAlertsCommentary, commentaryV2.alerts)) return;

        dispatch(
          setCommentaryV2({
            ...commentaryV2,
            alerts: newAlertsCommentary,
          }),
        );
      }, 300),
    [dispatch, commentaryV2],
  );

  useEffect(() => {
    // For some reason the form doesn't validate on mount, so we trigger it manually
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    zodForm.trigger();
  }, [dismissedAlertTypes, zodForm]);

  useEffect(() => {
    const subscription = watch((formValues) => {
      const cleanedFormData = Object.entries(formValues)
        .map(([k, v]) => [k, v === '' ? undefined : v?.trim()])
        .filter(([_k, v]) => v !== undefined) as [AlertType, string][];

      debouncedSetCommentary(
        cleanedFormData.length ? Object.fromEntries(cleanedFormData) : undefined,
      );
    });

    return () => subscription.unsubscribe();
  }, [dispatch, watch, debouncedSetCommentary]);

  if (Object.values(alerts).flat().length === 0) {
    return <Typography>🎉 Congrats, no alerts remain!</Typography>;
  }

  return (
    <FormProvider {...zodForm}>
      {Object.entries(alerts)
        .filter(([_section, sectionAlerts]) => sectionAlerts.length > 0)
        .map(([section, sectionAlerts]) => (
          <Flex key={section} flexDirection="column" gap="1rem">
            <Typography variant="h2">{ALERT_SECTION_LABELS[section as AlertSection]}</Typography>
            {sectionAlerts.map((alert, index) => (
              <AlertItem key={alert.type} alert={alert} index={index} />
            ))}
          </Flex>
        ))}
    </FormProvider>
  );
};
